import * as React from 'react'
import { useMemo } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import { AddFilled, AddRegular, bundleIcon, CalendarDayRegular, CalendarMonthRegular, CalendarWorkWeekRegular } from '@fluentui/react-icons'
import useModules from '../../../hooks/module/useModules'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import useUser from '../../../hooks/useUser'
import UserPicker from '../../../components/TagPicker/UserPicker'
import { ModuleType } from '../../../context/GlobalContext'
import AttributesList from '../../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../../hooks/useAxios'
import { SortDirection } from '../../../config/settingConfig'
import { AxiosResponse } from 'axios'
import { BaseOverviewCount, BaseOverviewLineItemProps, parseFilterValuesToParams, useOverviewStyles } from '../../../pages/BaseOverview/BaseOverview'
import { BaseOverviewFilterProps, BaseOverviewFilterType, generateFromDate, generateToDate } from '../../../pages/BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../../../pages/BaseOverview/BaseSortMenu'
import BaseMutableOverview, {
    BaseMutableOverviewActionButtonProps,
    BaseMutableOverviewProps,
} from '../../../pages/BaseMutableOverview/BaseMutableOverview'
import DateRange from '../../../components/DatePicker/DateRange'
import BooleanCombobox from '../../../components/Combobox/BooleanCombobox'
import FilePicker from '../../../components/TagPicker/FilePicker'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import { DateTime } from 'luxon'
import { TravelMutationDto, TravelOverviewDto } from '../../../config/registrationConfig'
import { MoneyDto } from '../../../config/i18nConfig'
import { Billable } from '../../../config/registration/timeConfig'
import { mapTravelOverviewDtoToMutation } from '../../../config/registration/travelConfig'
import { multiplyRate } from '../../../helpers/numberHelper'
import { formatIsoDate } from '../../../helpers/dateHelper'
import TravelListItem from './TravelListItem'
import TravelHeaderItem from './TravelHeaderItem'
import TravelMutationDialogBody from '../../components/Dialog/Registration/TravelMutationDialogBody'
import BillablePicker from '../../../components/TagPicker/BillablePicker'

const AddIcon = bundleIcon(AddFilled, AddRegular)

export const useItemStyles = makeStyles({
    listTravelItem: {
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) var(--baseOverviewTimeWidth) auto var(--baseOverviewFileWidth) var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth)',
    },
    listTravelHeaderItem: {
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) auto var(--baseOverviewFileWidth) var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth)',
    },
    listTravelHeaderItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewDateWidth) auto',
    },
    listTravelItemSelectable: {
        gridTemplateColumns:
            'var(--baseOverviewCheckboxWidth) var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) var(--baseOverviewTimeWidth) auto min-content var(--baseOverviewPriceWidth) min-content',
    },
    listTravelItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto auto',
    },
    listTravelItemSmallSelectable: {
        gridTemplateColumns: 'var(--baseOverviewCheckboxWidth) var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto auto',
    },
    listTravelItemSmallBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'auto var(--baseOverviewPriceWidth)',
        gap: tokens.spacingVerticalS,
    },
    listTravelItemOverflow: {
        overflowWrap: 'anywhere',
    },
    turnoverField: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingVerticalS,
    },
    billableIconSize: {
        fontSize: tokens.fontSizeBase400,
        paddingTop: '3px',
    },
    colorGreen: {
        color: tokens.colorPaletteGreenForeground1,
    },
    colorRed: {
        color: tokens.colorPaletteRedForeground1,
    },
})

type BaseItem = TravelOverviewDto

export type OverviewItem = {
    // Is calculated during runtime and not returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
} & BaseItem

export const getTravelTurnover = (rate: MoneyDto, item: OverviewItem): MoneyDto => {
    if (!rate) {
        return undefined
    }

    if (!item || (item.billable && item.billable !== Billable.Billable)) {
        return {
            amount: '0.00',
            currency: rate.currency,
        }
    }

    // An item only has the turnover property after the filters have applied during the mounting of this TravelList,
    // therefore calculate it when no turnover has been calculated.
    return multiplyRate(item.distance, rate)
}

type TravelOverviewProps = BaseMutableOverviewProps<ExtendedTravelOverviewDto, TravelFilterType, TravelSortingType, TravelOverviewCount>

export type BaseTravelOverviewProps = Partial<TravelOverviewProps> & {
    baseUrl: string

    fileId?: string
    hideFileField?: boolean
    hideInvoiceField?: boolean
    renderLink?: boolean
    // Overrides for the initial values of the create dialog (e.g. a date within a fixed interval).
    mutationDefaultValues?: Partial<TravelMutationDto>
}

export type BaseTravelLineOverviewProps = BaseTravelOverviewProps &
    BaseOverviewLineItemProps<ExtendedTravelOverviewDto, TravelFilterType, TravelSortingType>

export type TravelOverviewCount = BaseOverviewCount & {
    distance: number
    currencies: TravelCurrencyOverviewCount[]
}

export type TravelCurrencyOverviewCount = {
    currency: string
    distance: number
    turnover: string
    invoiceTurnover: string
}

export type ExtendedTravelOverviewDto = TravelOverviewDto & {
    // Is calculated during runtime and not actually returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
}

export type TravelFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        from?: string
        to?: string
        users?: string[]
        isBilled?: boolean
        files?: string[]
        fileOwners?: string[]
        isInvoiceApproval?: boolean
        invoiceApprovals?: string[]
        billable?: Billable[]
    }

export type TravelSorting = {
    id: string
    date: string
    description: string
    'file.name': string
    'file.reference': string
}

export type TravelSortingType = BaseOverviewSortingType<TravelSorting>

type BaseTravelOverviewFilterProps = BaseTravelOverviewProps & BaseOverviewFilterProps<ExtendedTravelOverviewDto, TravelFilterType, TravelSortingType>

function TravelFilter(props: BaseTravelOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<TravelFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const orientation = useMemo(() => (props.viewportMedium ? 'vertical' : 'horizontal'), [props.viewportMedium])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('registration.filter.user')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.users || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('users', selectedOptions)}
                />
            </Field>
            <Field label={translate('registration.filter.billable')} orientation={orientation}>
                <BillablePicker
                    selectedOptions={values.billable || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('billable', selectedOptions as Billable[])}
                />
            </Field>
            {!values.isInvoiceApproval && modules.has(ModuleType.Invoices) && (
                <Field label={translate('registration.filter.isBilled')} orientation={orientation}>
                    <BooleanCombobox
                        selectedOptions={[values.isBilled?.toString() || 'mixed']}
                        onOptionSelect={(_, data: CheckboxOnChangeData) =>
                            updateFieldValue('isBilled', data.checked === 'mixed' ? undefined : data.checked)
                        }
                    />
                </Field>
            )}
            {!props.hideFileField && (
                <Field label={translate('registration.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={values.files || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('registration.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => updateFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}
const filterTagItems: TravelFilterType[] = [
    {
        from: DateTime.local().toISODate(),
        to: DateTime.local().toISODate(),
        tag: 'day',
        icon: <CalendarDayRegular />,
        title: 'disbursement.filter.today',
    },
    {
        from: DateTime.local().startOf('week').toISODate(),
        to: DateTime.local().endOf('week').toISODate(),
        tag: 'week',
        icon: <CalendarWorkWeekRegular />,
        title: 'disbursement.filter.week',
    },
    {
        from: DateTime.local().startOf('month').toISODate(),
        to: DateTime.local().endOf('month').toISODate(),
        tag: 'month',
        icon: <CalendarMonthRegular />,
        title: 'disbursement.filter.month',
    },
]

const headerSortingItems: TravelSortingType[] = [
    { property: ['date', 'id'], direction: SortDirection.Ascending },
    { property: ['date', 'id'], direction: SortDirection.Descending, default: true },
    { property: ['description', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.descriptionaz' },
    { property: ['description', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.descriptionza' },
    { property: ['file.name', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.fileaz' },
    { property: ['file.name', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.fileza' },
    { property: ['file.reference', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.referenceasc' },
    {
        property: ['file.reference', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

function HeaderFilterTags(props: TravelOverviewProps) {
    const { values, setValues } = useFormikContext<TravelFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const viewportNarrow = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    return (
        <div className={overviewStyles.flexGapXS}>
            {filterTagItems.map((tag, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === tag.tag}
                    onClick={() =>
                        setValues({
                            ...props.defaultFilterValues,
                            ...tag,
                        })
                    }
                    size={viewportNarrow ? 'small' : 'medium'}
                >
                    {viewportNarrow && (
                        <Tooltip withArrow relationship={'description'} content={translate(tag.title)}>
                            <InteractionTagPrimary icon={tag.icon} />
                        </Tooltip>
                    )}
                    {!viewportNarrow && <InteractionTagPrimary icon={tag.icon}>{translate(tag.title)}</InteractionTagPrimary>}
                </InteractionTag>
            ))}
        </div>
    )
}

function HeaderActionsButtons(
    props: BaseOverviewFilterProps<
        ExtendedTravelOverviewDto,
        TravelFilterType,
        TravelSortingType,
        TravelOverviewCount,
        BaseMutableOverviewActionButtonProps<any>
    >,
) {
    const { t: translate } = useTranslation()

    return (
        <Tooltip withArrow relationship={'label'} content={translate('action.create.travel.title')}>
            <Button icon={<AddIcon />} appearance={'primary'} onClick={() => props.setOpenMutationDialog(true, null)} />
        </Tooltip>
    )
}

/**
 * The mutable travel overview (HAM-3627): a BaseMutableOverview with the filter, the add/edit popup
 * and a create button wired in — like BaseTimeOverview. The dossier wrapper (TravelOverview) scopes
 * to the file; the declaration advice passes selection handlers and its own header but keeps the
 * create button.
 */
export default function BaseTravelOverview(props: BaseTravelOverviewProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const { currentUser } = useUser()

    const defaultFilterValues = useMemo<TravelFilterType>(
        () => ({
            from: '',
            to: '',
            users: [],
            attributes: [],
            query: '',
            tag: undefined,
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const countItems = (_column?: number, filterValues?: TravelFilterType): Promise<TravelOverviewCount> => {
        return axios.get('/travel/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        _column?: number,
        filterValues?: TravelFilterType,
        sortingValues?: TravelSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedTravelOverviewDto[]> => {
        return axios
            .get('/travel', { params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) } })
            .then((response: AxiosResponse<OverviewItem[]>): ExtendedTravelOverviewDto[] =>
                (response?.data || []).map((item: OverviewItem) => ({
                    ...item,
                    turnover: getTravelTurnover(item.rate, item),
                    invoiceTurnover: item.price && {
                        amount: item.price.subtotal,
                        currency: item.price.currency,
                    },
                })),
            )
    }

    return (
        <BaseMutableOverview<
            ExtendedTravelOverviewDto,
            TravelFilterType,
            TravelSortingType,
            TravelOverviewCount,
            BaseMutableOverviewActionButtonProps<ExtendedTravelOverviewDto>,
            any,
            TravelMutationDto
        >
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={TravelHeaderItem}
            listItemHeaderClassnames={props => [itemStyles.listTravelHeaderItem, props.viewportNarrow && itemStyles.listTravelHeaderItemSmall]}
            listItemContent={(itemProps: BaseTravelLineOverviewProps) => <TravelListItem {...itemProps} />}
            listItemClassnames={props => [
                props.onItemSelected ? itemStyles.listTravelItemSelectable : itemStyles.listTravelItem,
                props.viewportNarrow && (props.onItemSelected ? itemStyles.listTravelItemSmallSelectable : itemStyles.listTravelItemSmall),
            ]}
            headerDefaultFilterTags={HeaderFilterTags}
            headerSortingItems={headerSortingItems}
            headerActionsButtons={HeaderActionsButtons}
            filterContent={TravelFilter}
            filterFromJson={values => ({
                ...values,
                from: values.tag ? generateFromDate(values.tag) : values.from,
                to: values.tag ? generateToDate(values.tag) : values.to,
            })}
            filterToJson={values => ({
                ...values,
                from: values.tag ? '' : values.from,
                to: values.tag ? '' : values.to,
                icon: undefined,
                title: undefined,
            })}
            inputPlaceholderKey={'travel.filter.search'}
            mutationDialogFormProps={item => ({
                initialValues: item
                    ? mapTravelOverviewDtoToMutation(item)
                    : {
                          description: undefined,
                          fileId: props.fileId,
                          userId: currentUser?.id,
                          billable: Billable.Billable,
                          date: formatIsoDate(new Date()),
                          distance: undefined,
                          currency: undefined,
                          rate: undefined,
                          ...props.mutationDefaultValues,
                      },
                onSubmit: () => {},
            })}
            mutationDialogBodyProps={item => ({ item })}
            mutationDialogBody={TravelMutationDialogBody}
            mutationDialogProps={{ warnOnUnsavedChanges: true }}
            {...props}
        />
    )
}
